import axios, { AxiosRequestConfig, AxiosRequestHeaders, AxiosResponseHeaders, Method } from 'axios';

export interface IRequest<T = any> {
    url: string;
    baseURL: string;
    data?: T;
}

export interface IResponse<D = any> {
    data: D;
    status: number;
    statusText: string;
    headers: TResponseHeaders;
    config: IRequestConfig;
    request?: any;
}

export type TRequestHeaders = AxiosRequestHeaders;

interface IRequestConfig extends AxiosRequestConfig {}

type TResponseHeaders = AxiosResponseHeaders;

type TMethod = Method;

const httpClient = () => {
    const _instance = axios.create({
        timeout: parseInt(process.env.TIMEOUT_HTTPCLIENT as string),
        withCredentials: (process.env.ENVIRONMENT as string) === 'prod' ? true : false,
    });

    const _createHeaders = (headers?: TRequestHeaders) => {
        return {
            [process.env.CONTENT_TYPE as string]: headers?.contentType || 'application/json',
            ...headers,
        };
    };

    const _createRequest = <T = any>(method: TMethod, request: IRequest<T>, headers?: TRequestHeaders): AxiosRequestConfig<T> => {
        return {
            method: method,
            baseURL: request.baseURL,
            url: request.url,
            data: request.data,
            headers: _createHeaders(headers),
        };
    };

    _instance.interceptors.response.use(
        res => res,
        error => {
            if (error.response.status === 401) {
                return (window.location.href = process.env.EDESIGN_PORTAL_URL as any);
            }
            // Any status codes that falls outside the range of 2xx cause this function to trigger
            // Do something with response error
            // You can also log the error to an error reporting service
            return Promise.reject(error);
        },
    );

    const getAsync = async <D = any>(request: IRequest, headers?: TRequestHeaders): Promise<IResponse<D>> => {
        return _instance.request(_createRequest('get', request, headers));
    };

    const postAsync = async <T = any, D = any>(request: IRequest<T>, headers?: TRequestHeaders): Promise<IResponse<D>> => {
        return _instance.request(_createRequest('post', request, headers));
    };

    const putAsync = async <T = any, D = any>(request: IRequest<T>, headers?: TRequestHeaders): Promise<IResponse<D>> => {
        return _instance.request(_createRequest('put', request, headers));
    };

    const deleteAsync = async <D = any>(request: IRequest, headers?: TRequestHeaders): Promise<IResponse<D>> => {
        return _instance.request(_createRequest('delete', request, headers));
    };

    return {
        getAsync,
        postAsync,
        putAsync,
        deleteAsync,
    };
};

export default httpClient();
